import { Action } from '@reduxjs/toolkit';

import { DeepPartial } from 'types/util';

import { SrpFilterName, SrpFiltersState, SrpFilterValue, SrpFilterValues } from 'types/srpFilters';

export enum SrpActionType {
  SetFilters = 'srpFilters/setFilters',
  ClearFilters = 'srpFilters/clearFilters',
  ToggleSidebarOpen = 'srpFilters/toggleSidebarOpen',
}

export interface SetSrpFiltersAction {
  type: SrpActionType.SetFilters;
  payload: DeepPartial<SrpFilterValues>;
}

export function setSrpFilters(filters: DeepPartial<SrpFilterValues>): SetSrpFiltersAction {
  let payload = filters;

  if (!payload.Page) {
    payload = { ...filters, [SrpFilterName.Page]: 1 }; // reset the page to 1 when other filters change
  }

  return {
    type: SrpActionType.SetFilters,
    payload,
  };
}

export interface ClearSrpFiltersAction {
  type: SrpActionType.ClearFilters;
}

export function clearSrpFilters(): ClearSrpFiltersAction {
  return {
    type: SrpActionType.ClearFilters,
  };
}

export interface ToggleSrpSidebarOpenAction {
  type: SrpActionType.ToggleSidebarOpen;
}

export function toggleSrpSidebarOpen(): ToggleSrpSidebarOpenAction {
  return {
    type: SrpActionType.ToggleSidebarOpen,
  };
}

export const srpFilterInitialValues: SrpFilterValues = {
  [SrpFilterName.Page]: 1,
  [SrpFilterName.Limit]: 20,
  [SrpFilterName.Year]: {
    min: 1940,
    max: new Date().getFullYear() + 1,
  },
  [SrpFilterName.Condition]: {
    new: false,
    'certified pre-owned': false,
    used: false,
  },
  [SrpFilterName.Cylinders]: {
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    8: false,
    10: false,
    12: false,
  },
  [SrpFilterName.MakeModel]: { make: undefined, model: undefined },
  [SrpFilterName.MultiMakeModel]: [
    { make: undefined, model: undefined },
    { make: undefined, model: undefined },
    { make: undefined, model: undefined },
  ],
  [SrpFilterName.Trims]: [],
  [SrpFilterName.Radius]: 100,
  [SrpFilterName.SortBy]: undefined,
  [SrpFilterName.Price]: {
    hideNoPriceListings: false,
    min: 0,
    max: 100000,
  },
  [SrpFilterName.Doors]: {
    2: false,
    3: false,
    4: false,
  },
  [SrpFilterName.Advanced]: {
    hideOnlineListings: false,
    hideListingsNoImages: false,
  },
  [SrpFilterName.BodyTypes]: {
    convertible: false,
    coupe: false,
    hatchback: false,
    minivan: false,
    sedan: false,
    suv: false,
    truck: false,
    wagon: false,
  },
  [SrpFilterName.Colors]: {
    exterior: {
      white: false,
      gray: false,
      black: false,
      yellow: false,
      orange: false,
      brown: false,
      red: false,
      green: false,
      blue: false,
      purple: false,
      silver: false,
      gold: false,
    },
    interior: {
      white: false,
      gray: false,
      black: false,
      brown: false,
      red: false,
      blue: false,
    },
  },
  [SrpFilterName.Drivetrain]: {
    frontWheel: false,
    rearWheel: false,
    allWheel: false,
    fourWheel: false,
  },
  [SrpFilterName.Powertrain]: {
    electric: false,
    diesel: false,
    gasoline: false,
    hybrid: false,
    hydrogenFuelCell: false,
    naturalGas: false,
    pluginHybrid: false,
  },
  [SrpFilterName.FuelEfficiency]: 0,
  [SrpFilterName.FeaturesTechnology]: {
    backupCamera: false,
    bluetooth: false,
    entertainmentDVD: false,
    handicapAccessible: false,
    iPodAUXInput: false,
    laneDepartureWarning: false,
    leather: false,
    navigationSystem: false,
    roofRack: false,
    sunroofMoonroof: false,
    '3rdRowSeats': false,
    towing: false,
  },
  [SrpFilterName.Location]: {
    city: undefined,
    stateCode: undefined,
    latitude: undefined,
    longitude: undefined,
  },
  [SrpFilterName.Mileage]: {
    min: 0,
    max: 300000,
  },
  [SrpFilterName.Transmission]: {
    manual: false,
    automatic: false,
  },
  [SrpFilterName.TruckOptions]: {
    cabinType: {
      extended: false,
      regular: false,
      crew: false,
    },
    rearWheel: {
      single: false,
      dual: false,
    },
    bedType: {
      short: false,
      regular: false,
      chassis: false,
      stepSide: false,
      long: false,
    },
    bedLength: {
      min: 3,
      max: 9,
    },
  },
  [SrpFilterName.Category]: 'Any Category',
  [SrpFilterName.SortNewCarsLast]: undefined,
};
export const srpFiltersInitialState: SrpFiltersState = {
  initialized: false,
  values: srpFilterInitialValues,
  mobileSidebarOpen: false,
};

export function srpFiltersReducer(
  state: SrpFiltersState = srpFiltersInitialState,
  action: Action
): SrpFiltersState {
  if (!Object.values(SrpActionType).includes(action.type)) {
    return state;
  }

  if (action.type === SrpActionType.SetFilters) {
    const setFiltersAction = action as SetSrpFiltersAction;
    const values = mergeDeep<SrpFilterValues>({ ...state.values }, setFiltersAction.payload);
    return {
      ...state,
      initialized: true,
      values,
    };
  }

  if (action.type === SrpActionType.ClearFilters) {
    return {
      ...state,
      values: {
        ...srpFilterInitialValues,
        [SrpFilterName.Location]: state.values[SrpFilterName.Location],
      },
    };
  }

  if (action.type === SrpActionType.ToggleSidebarOpen) {
    return {
      ...state,
      mobileSidebarOpen: !state.mobileSidebarOpen,
    };
  }

  return state;
}

function isObject(value: unknown): boolean {
  const type = typeof value;
  return value != null && (type === 'object' || type === 'function');
}

function mergeDeep<T>(...objects: DeepPartial<T>[]): T {
  return objects.reduce((prev: any, obj: any) => {
    Object.keys(obj).forEach((key) => {
      /* eslint-disable no-param-reassign */
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = oVal;
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
}

export function getSrpFiltersInitialized(state: SrpFiltersState): boolean {
  return state.initialized;
}

export function getSrpFilterValues(state: SrpFiltersState): SrpFilterValues {
  return state.values;
}

export function getSrpFilterValue<T extends SrpFilterValue>(
  state: SrpFiltersState,
  name: SrpFilterName
): T {
  return state.values[name] as T;
}

export function getSrpMobileSidebarOpen(state: SrpFiltersState): boolean {
  return state.mobileSidebarOpen;
}
